import React, { useEffect, useRef, useState } from 'react'
import { AdMediaItem, AdMediaType } from '@marketing-milk/interfaces'
import { videoStillURL } from 'components/ad-preview'
import {
  MODAL_SCROLL_CLASS,
  PacketModalShell,
  modalGhostButtonStyle,
  modalInputStyle,
  modalPrimaryButtonStyle,
  modalScrollFadeStyle,
} from '../components/PacketModalShell'
import { SurfacePalette, radius, status as statusColors, type as typo } from '../tokens'
import { IconButton } from './MediaControls'
import { imageMedia, mediaThumb, videoMedia } from './mediaDraft'
import { MediaLibrary } from './reviewMediaLibrary'

const FILTERS: Array<{ label: string; mediaType?: AdMediaType }> = [
  { label: 'All' },
  { label: 'Images', mediaType: 'image' },
  { label: 'Videos', mediaType: 'video' },
]

const SEARCH_DELAY_MS = 250
/** How close to the bottom the grid gets before the next page is asked for. */
const LOAD_AHEAD_PX = 300

const itemKey = (item: AdMediaItem) => `${item.mediaType}:${item.asset.id}`

const itemName = (item: AdMediaItem) =>
  item.mediaType === 'image'
    ? item.asset.name || 'Untitled image'
    : item.asset.title || 'Untitled video'

const duration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`

const deleteFailure = (error: any) =>
  error?.response?.status === 409
    ? error.response.data.message
    : "Couldn't delete that image. Try again in a moment."

/**
 * The business's stored media, to pick from instead of uploading again. Upload
 * sits in the header so a fresh file is still one click from the editor — the
 * shape of Meta's "Add media" dialog, with the library on show.
 */
export function MediaLibraryDialog({
  palette,
  library,
  draftImageIDs,
  onPick,
  onUpload,
  onClose,
}: {
  palette: SurfacePalette
  library: MediaLibrary
  draftImageIDs?: ReadonlySet<number>
  onPick: (item: AdMediaItem) => void
  onUpload: () => void
  onClose: () => void
}) {
  const [mediaType, setMediaType] = useState<AdMediaType | undefined>(undefined)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<AdMediaItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState<number | undefined>(undefined)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  /** Bumped per query, so a page that lands after the query changed is dropped. */
  const generation = useRef(0)
  /** A ref, not `loading`: scroll events can outrun the render that sets it. */
  const inFlight = useRef(false)

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const load = async (offset: number) => {
    const current = generation.current
    inFlight.current = true
    setLoading(true)
    setLoadError(false)
    try {
      const page = await library.list({ mediaType, search, offset })
      if (generation.current !== current) return
      setItems(existing => {
        if (offset === 0) return page.items
        // Media synced in from Ads Manager mid-browse lands at the top and pushes
        // the next page's offset back over items already loaded.
        const loaded = new Set(existing.map(itemKey))
        return [...existing, ...page.items.filter(item => !loaded.has(itemKey(item)))]
      })
      setHasMore(page.hasMore)
    } catch {
      if (generation.current === current) setLoadError(true)
    } finally {
      if (generation.current === current) {
        inFlight.current = false
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    generation.current += 1
    setItems([])
    load(0)
    // `load` reads the query it is re-run for; `library` is a fresh object per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType, search])

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget
    if (!hasMore || inFlight.current || loadError) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_AHEAD_PX) load(items.length)
  }

  const deleteImage = async (imageID: number) => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await library.deleteImage(imageID)
      setItems(existing =>
        existing.filter(item => !(item.mediaType === 'image' && item.asset.id === imageID))
      )
      setConfirmingDelete(undefined)
    } catch (error) {
      setDeleteError(deleteFailure(error))
      setConfirmingDelete(undefined)
    } finally {
      setDeleting(false)
    }
  }

  const note = (text: string, color = palette.textMuted) => (
    <p
      style={{ margin: 0, padding: '24px 0', textAlign: 'center', fontSize: typo.size.body, color }}
    >
      {text}
    </p>
  )

  return (
    <PacketModalShell
      palette={palette}
      title="Media library"
      headerActions={
        <button
          type="button"
          onClick={onUpload}
          style={{ ...modalPrimaryButtonStyle(palette), flex: 'none' }}
        >
          <i className="fa fa-upload" style={{ color: 'inherit' }} aria-hidden="true" />
          Upload from computer
        </button>
      }
      onClose={onClose}
      closeOnBackdropClick
      maxWidth={880}
      fillHeight
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search"
            aria-label="Search media"
            placeholder="Search by name"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            style={{ ...modalInputStyle(palette), flex: 1, minWidth: 200, width: 'auto' }}
          />
          <div
            role="tablist"
            aria-label="Media type"
            style={{
              display: 'flex',
              padding: 2,
              gap: 2,
              borderRadius: radius.sm,
              border: `1px solid ${palette.hairline}`,
              background: palette.bg,
            }}
          >
            {FILTERS.map(filter => {
              const on = filter.mediaType === mediaType
              return (
                <button
                  key={filter.label}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setMediaType(filter.mediaType)}
                  style={{
                    padding: '5px 14px',
                    borderRadius: radius.sm - 2,
                    border: 'none',
                    background: on ? palette.surface : 'transparent',
                    fontFamily: typo.family.sans,
                    fontSize: typo.size.body,
                    fontWeight: on ? typo.weight.semibold : typo.weight.regular,
                    color: on ? palette.text : palette.textMuted,
                    cursor: 'pointer',
                  }}
                >
                  {filter.label}
                </button>
              )
            })}
          </div>
        </div>

        {deleteError && (
          <p
            role="alert"
            style={{ margin: 0, fontSize: typo.size.body, color: palette.status.danger }}
          >
            {deleteError}
          </p>
        )}

        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <div
            className={MODAL_SCROLL_CLASS}
            onScroll={onScroll}
            style={{ height: '100%', overflowY: 'auto', paddingBottom: 28 }}
          >
            <div
              role="list"
              aria-label="Media"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 16,
              }}
            >
              {items.map(item => (
                <LibraryTile
                  key={itemKey(item)}
                  item={item}
                  palette={palette}
                  confirmingDelete={
                    item.mediaType === 'image' && confirmingDelete === item.asset.id
                  }
                  deleting={deleting}
                  usedInDraft={item.mediaType === 'image' && !!draftImageIDs?.has(item.asset.id)}
                  onPick={() => onPick(item)}
                  onAskDelete={() => {
                    setDeleteError(null)
                    setConfirmingDelete(item.asset.id)
                  }}
                  onCancelDelete={() => setConfirmingDelete(undefined)}
                  onConfirmDelete={() => deleteImage(item.asset.id)}
                />
              ))}
            </div>
            {loading && note('Loading…')}
            {loadError && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                {note("The library didn't load.", palette.status.danger)}
                <button
                  type="button"
                  onClick={() => load(items.length)}
                  style={modalGhostButtonStyle(palette)}
                >
                  Try again
                </button>
              </div>
            )}
            {!loading &&
              !loadError &&
              items.length === 0 &&
              note(
                search
                  ? `Nothing matches “${search}”.`
                  : 'Nothing here yet. Upload a file and it will be kept here for next time.'
              )}
          </div>
          <div style={modalScrollFadeStyle(palette)} />
        </div>
      </div>
    </PacketModalShell>
  )
}

function LibraryTile({
  item,
  palette,
  confirmingDelete,
  deleting,
  usedInDraft,
  onPick,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  item: AdMediaItem
  palette: SurfacePalette
  confirmingDelete: boolean
  deleting: boolean
  usedInDraft: boolean
  onPick: () => void
  onAskDelete: () => void
  onCancelDelete: () => void
  onConfirmDelete: () => void
}) {
  const name = itemName(item)
  const thumb = mediaThumb(
    item.mediaType === 'image' ? imageMedia(item.asset) : videoMedia(item.asset)
  )
  const fill: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' }
  // Drawn over the media itself, so it keeps the same dark scrim on either palette.
  const overButton: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: radius.sm,
    fontSize: typo.size.eyebrow,
    fontWeight: typo.weight.semibold,
    cursor: deleting ? 'default' : 'pointer',
  }

  return (
    <div role="listitem" style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div style={{ position: 'relative', aspectRatio: '1 / 1' }}>
        <button
          type="button"
          aria-label={`Use ${name}`}
          onClick={onPick}
          disabled={confirmingDelete}
          style={{
            width: '100%',
            height: '100%',
            padding: 0,
            overflow: 'hidden',
            borderRadius: radius.sm,
            border: `1px solid ${palette.hairline}`,
            background: palette.bg,
            cursor: 'pointer',
          }}
        >
          {thumb?.kind === 'image' && <img src={thumb.url} alt="" style={fill} />}
          {thumb?.kind === 'video' && (
            <video
              src={videoStillURL(thumb.url)}
              muted
              playsInline
              preload="metadata"
              tabIndex={-1}
              style={{ ...fill, pointerEvents: 'none', background: '#000' }}
            />
          )}
        </button>
        {item.mediaType === 'video' && (
          <span
            style={{
              position: 'absolute',
              left: 6,
              bottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 6px',
              borderRadius: radius.sm,
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontSize: typo.size.eyebrow,
              pointerEvents: 'none',
            }}
          >
            <i
              className="fa fa-play"
              style={{ color: 'inherit', fontSize: 8 }}
              aria-hidden="true"
            />
            {item.asset.length ? duration(item.asset.length) : 'Video'}
          </span>
        )}
        {confirmingDelete && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: 8,
              borderRadius: radius.sm,
              background: 'rgba(0,0,0,0.75)',
              color: '#fff',
              textAlign: 'center',
              fontSize: typo.size.eyebrow,
            }}
          >
            Delete from the ad account too?
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={onConfirmDelete}
                disabled={deleting}
                style={{
                  ...overButton,
                  border: 'none',
                  background: statusColors.danger,
                  color: '#fff',
                }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                onClick={onCancelDelete}
                disabled={deleting}
                style={{
                  ...overButton,
                  border: '1px solid rgba(255,255,255,0.5)',
                  background: 'transparent',
                  color: '#fff',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          minWidth: 0,
          color: palette.textMuted,
        }}
      >
        <span
          title={name}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: typo.size.eyebrow,
            color: palette.textMuted,
          }}
        >
          {name}
        </span>
        {/* Images only: the backend has no way to delete a video. */}
        {item.mediaType === 'image' && (
          <IconButton
            icon="trash"
            label={`Delete ${name}`}
            onClick={onAskDelete}
            disabled={deleting || confirmingDelete || usedInDraft}
            title={usedInDraft ? 'This image is used by the open draft.' : undefined}
          />
        )}
      </div>
    </div>
  )
}
